import { useEffect, useRef } from "react";
import { AppState, LayoutChangeEvent, NativeScrollEvent, NativeSyntheticEvent } from "react-native";

/** One scroll owner; layout and request completion also recheck short feeds. */
export function useFeedDemand({ scope, busy, hasMore, loadMore, prefetchMore }: {
  scope: string; busy: boolean; hasMore: boolean;
  loadMore: () => void; prefetchMore: () => void;
}) {
  const metrics = useRef({ height: 0, content: 0, y: 0 });
  const budget = useRef(0);
  const scheduled = useRef(false);
  const mounted = useRef(true);
  const latest = useRef({ busy, hasMore, loadMore, prefetchMore });
  latest.current = { busy, hasMore, loadMore, prefetchMore };
  const check = () => {
    if (scheduled.current) return;
    scheduled.current = true;
    requestAnimationFrame(() => {
      scheduled.current = false;
      if (!mounted.current) return;
      if ((AppState.currentState != null && AppState.currentState !== "active") ||
          (typeof document !== "undefined" && document.visibilityState === "hidden")) return;
      const { height, content, y } = metrics.current;
      const current = latest.current;
      if (!height || !content || !current.hasMore || current.busy) return;
      const remaining = content - height - y;
      if (remaining <= height * 1.5) current.prefetchMore();
      if (remaining <= height * .5 && budget.current < 3) {
        budget.current++;
        current.loadMore();
      }
    });
  };
  useEffect(() => { budget.current = 0; check(); }, [scope]);
  useEffect(check, [busy, hasMore, loadMore]);
  useEffect(() => {
    mounted.current = true;
    const subscription = AppState.addEventListener("change", state => { if (state === "active") check(); });
    return () => { mounted.current = false; subscription.remove(); };
  }, []);
  return {
    onLayout: (event: LayoutChangeEvent) => { metrics.current.height = event.nativeEvent.layout.height; check(); },
    onContentSizeChange: (_width: number, height: number) => { metrics.current.content = height; check(); },
    onScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const n = event.nativeEvent;
      // Movement, rather than repeated bottom notifications, opens a new burst.
      if (Math.abs(metrics.current.y - n.contentOffset.y) > 2) budget.current = 0;
      metrics.current = { height: n.layoutMeasurement.height, content: n.contentSize.height, y: n.contentOffset.y };
      check();
    },
    manualLoadMore: () => { budget.current = 0; latest.current.loadMore(); },
  };
}
